import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Modal, Form, Button, Alert, Spinner, Badge } from 'react-bootstrap'
import {
  useAdminDatumUserReports,
  useUpdateAdminDatumUserReports
} from 'api/hooks/use-admin-datum'

const DatumUserReportsModal = ({ municipalityId, user, show, onHide }) => {
  const userId = user?.id
  const [selectedIds, setSelectedIds] = useState([])
  const [saveError, setSaveError] = useState(null)

  const { data, isLoading, error } = useAdminDatumUserReports({
    municipalityId,
    userId,
    options: { enabled: !!show && !!userId }
  })

  const updateReports = useUpdateAdminDatumUserReports({
    municipalityId,
    userId,
    options: {
      onSuccess: () => onHide(),
      onError: (err) => setSaveError(err?.message || 'Failed to save report assignments')
    }
  })

  const reports = data?.reports || []
  const directReportIds = data?.direct_report_ids || []
  const tagReportIds = data?.tag_report_ids || []
  const accessibleReportIds = data?.accessible_report_ids || []

  // Clear any carried-over selection/error the moment we switch users, so the
  // previously-opened user's data is never shown under the new user's name.
  useEffect(() => {
    setSelectedIds([])
    setSaveError(null)
  }, [userId])

  // Populate the working selection whenever fresh data arrives for the user.
  useEffect(() => {
    if (data) setSelectedIds(data.direct_report_ids || [])
  }, [data])

  const reportName = (reportId) => {
    const report = reports.find(r => r.id === reportId)
    return report ? report.name : reportId.slice(0, 8)
  }

  const toggleReportId = (reportId) => {
    setSaveError(null)
    setSelectedIds(prev =>
      prev.includes(reportId) ? prev.filter(id => id !== reportId) : [...prev, reportId]
    )
  }

  const handleSave = (e) => {
    e.preventDefault()
    setSaveError(null)
    updateReports.mutate({ report_ids: selectedIds })
  }

  const userName = user ? `${user.first_name} ${user.last_name}` : ''
  const unrestricted = accessibleReportIds.length === 0

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Reports for {userName}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body>
          {isLoading && (
            <div className="text-center p-4">
              <Spinner animation="border" size="sm" />
            </div>
          )}

          {error && (
            <Alert variant="danger">Failed to load this user&apos;s reports.</Alert>
          )}

          {!isLoading && !error && (
            <>
              {saveError && <Alert variant="danger">{saveError}</Alert>}

              <h6 className="border-bottom pb-2 mb-3">Reports this user can access</h6>
              {unrestricted ? (
                <Alert variant="info">
                  This user has no tags or one-off reports assigned, so they can access
                  <strong> all reports</strong> in the workspace. Assign a tag or an individual
                  report below to restrict their access.
                </Alert>
              ) : (
                <div className="mb-4">
                  {accessibleReportIds.map((rid) => (
                    <div key={rid} className="d-flex align-items-center mb-1">
                      <span className="me-2">{reportName(rid)}</span>
                      {tagReportIds.includes(rid) && (
                        <Badge bg="info" className="me-1">Tag</Badge>
                      )}
                      {directReportIds.includes(rid) && (
                        <Badge bg="primary">Direct</Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <h6 className="border-bottom pb-2 mb-3">Assign individual reports (one-off)</h6>
              {reports.length > 0 ? (
                <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                  {reports.map((report) => (
                    <Form.Check
                      key={report.id}
                      type="checkbox"
                      id={`user-report-${report.id}`}
                      checked={selectedIds.includes(report.id)}
                      onChange={() => toggleReportId(report.id)}
                      className="mb-1"
                      label={(
                        <span>
                          {report.name}
                          {tagReportIds.includes(report.id) && (
                            <Badge bg="light" text="primary" className="ms-2">via tag</Badge>
                          )}
                        </span>
                      )}
                    />
                  ))}
                </div>
              ) : (
                <Alert variant="warning" className="mb-0">
                  No reports available. Ensure a Power BI workspace is configured.
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light-primary" onClick={onHide}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={updateReports.isLoading || isLoading}>
            {updateReports.isLoading ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

DatumUserReportsModal.propTypes = {
  municipalityId: PropTypes.string.isRequired,
  user: PropTypes.shape({
    id: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string
  }),
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired
}

export default DatumUserReportsModal
